import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Chip,
  alpha,
} from '@mui/material'
import {
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  Publish,
} from '@mui/icons-material'
import { useState } from 'react'
import { usePublishReadiness } from '../../hooks/usePublishReadiness'
import { usePublishedPermissions } from '../../hooks/usePublishedPermissions'

interface PublishReadinessChecklistProps {
  /** Whether to show in collapsed mode by default */
  defaultCollapsed?: boolean
  /** Callback when user clicks the "Go to Publish" action */
  onNavigateToPublish?: () => void
}

/**
 * Displays a checklist of requirements for publishing an adventure.
 * Shows which requirements are met and which are still pending.
 */
export function PublishReadinessChecklist({
  defaultCollapsed = false,
  onNavigateToPublish,
}: PublishReadinessChecklistProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const { isReady, items } = usePublishReadiness()
  const { isPublished, canPublish } = usePublishedPermissions()

  // Don't show if already published
  if (isPublished) {
    return null
  }

  const metCount = items.filter((item) => item.met).length
  const totalCount = items.length

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: isReady ? 'success.light' : 'warning.light',
        backgroundColor: isReady
          ? (theme) => alpha(theme.palette.success.light, 0.05)
          : (theme) => alpha(theme.palette.warning.light, 0.05),
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={() => setCollapsed(!collapsed)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Publish
            sx={{
              color: isReady ? 'success.main' : 'warning.main',
              fontSize: 24,
            }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Publish Readiness
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isReady
                ? 'Ready to publish!'
                : `${metCount} of ${totalCount} requirements met`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={isReady ? 'Ready' : 'Not Ready'}
            color={isReady ? 'success' : 'warning'}
            sx={{ fontWeight: 500 }}
          />
          <IconButton size="small">
            {collapsed ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Box>
      </Box>

      {/* Checklist */}
      <Collapse in={!collapsed}>
        <List dense sx={{ pt: 0, pb: 1 }}>
          {items.map((item) => (
            <ListItem
              key={item.id}
              sx={{
                py: 0.5,
                px: 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.met ? (
                  <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                ) : (
                  <Cancel sx={{ color: 'error.main', fontSize: 20 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.description}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: item.met ? 400 : 500,
                  color: item.met ? 'text.secondary' : 'text.primary',
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  sx: {
                    color: item.met ? 'text.disabled' : 'text.secondary',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>

        {/* Help text */}
        {!isReady && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Complete all requirements above to enable the Publish action in the
              Actions menu.
            </Typography>
          </Box>
        )}

        {/* Ready state message */}
        {isReady && canPublish && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
              All requirements met! You can publish from the Actions menu.
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  )
}
